import type { MeasurementUnits, MeasurePoint } from './types.js';

// Defensive cap against raycast hits behind a tiny near plane that map
// to "infinity" — we ignore any hit beyond this distance from the camera
// (typical real-estate GS dataset is 10–30m diagonal).
export const MAX_RAYCAST_DISTANCE = 100;

const FT_PER_METER = 3.28084;

export function distanceWorld(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

// Per-segment cumulative distances. N points → N-1 segment lengths.
// Indices 0..N-2 correspond to segments points[i] → points[i+1].
export function cumulativeDistances(points: readonly MeasurePoint[]): number[] {
  if (points.length < 2) return [];
  const out: number[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (a === undefined || b === undefined) continue;
    out.push(distanceWorld(a.world, b.world));
  }
  return out;
}

export function formatDistance(meters: number, units: MeasurementUnits): string {
  if (!Number.isFinite(meters)) return '—';
  if (units === 'cm') {
    const cm = meters * 100;
    // Below 10 cm → 1 decimal; above → integer.
    return cm < 10 ? `${cm.toFixed(1)} cm` : `${Math.round(cm)} cm`;
  }
  if (units === 'ft') {
    const ft = meters * FT_PER_METER;
    return `${ft.toFixed(2)} ft`;
  }
  // 'm'
  return `${meters.toFixed(2)} m`;
}
